import { createSlice } from '@reduxjs/toolkit';
import { ProductState } from './product.interface';
import { StoreState } from '../../store/store.interface';
import environment from '../../common/environment';
import { getDefaultHeaders } from '../../common/utils/auth-utils';

// TODO Refer to this documentation for using reduxjs toolkit with Typescript: https://redux-toolkit.js.org/usage/usage-with-typescript

/*----- CREATE STORE ------*/
declare interface ProductSlice {
  name: string;
  initialState: ProductState;
  reducers: any;
}

const slice: ProductSlice = {
  name: 'product',
  initialState: {
    products: {}
  },
  reducers: {
    onSuccess: (state: ProductState, action: any) => {
      if (action.payload.id) {
        state.products[action.payload.id] = {
          data: action.payload.product,
          isLoaded: true,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          message: `Successfully loaded product with id ${action.payload.id}`
        }
      }
    },
    onFailure: (state: ProductState, action: any) => {
      state.products[action.payload] = {
        data: undefined,
        isLoaded: false,
        isLoading: false,
        isSuccess: false,
        isFailure: true,
        message: `Failed to load product with id: ${action.payload}`
      }
    },
    setLoading: (state: ProductState, action: any) => {
      state.products[action.payload] = {
        data: undefined,
        isLoaded: false,
        isLoading: true,
        isSuccess: false,
        isFailure: false,
        message: `Loading product with id: ${action.payload}`
      }
    }
  },
};

export const productSlice: any = createSlice(slice);

/*----- EXPORT ACTIONS ------*/
export const onSuccess: Function = productSlice.actions.onSuccess;
export const onFailure: Function = productSlice.actions.onFailure;
export const setLoading: Function = productSlice.actions.setLoading;

/*----- THUNKS ------*/

/**
 * This will only make an API request if the provided product is not already loaded
 */
export const setProductAsync = (businessUnit: string, isbn: string) => (dispatch: Function, getState: Function) => {
  if (!isLoading(getState(), isbn) && !isLoaded(getState(), isbn) && !isFailure(getState(), isbn)) {
    dispatch(setLoading(isbn));

    makeProductRequest(businessUnit, isbn)
      .then(product => dispatch(onSuccess({ id: isbn, product })))
      .catch(() => dispatch(onFailure(isbn)));
  }
};

/**
 * This will always make an API request.
 */
export const forceSetProductAsync = (businessUnit: string, isbn: string) => (dispatch: Function) => {
  dispatch(setLoading(isbn));

  makeProductRequest(businessUnit, isbn)
    .then(product => dispatch(onSuccess({ id: isbn, product })))
    .catch(() => dispatch(onFailure(isbn)))
};

/*----- SELECTORS ------*/
export const selectProduct = (state: StoreState, isbn: string) => state.productState.products[isbn]?.data;
export const hasProduct = (state: StoreState, isbn: string) => Object.keys(state.productState.products).includes(isbn);
export const isLoading = (state: StoreState, isbn: string) => state.productState.products[isbn]?.isLoading;
export const isLoaded = (state: StoreState, isbn: string) => state.productState.products[isbn]?.isLoaded;
export const isSuccess = (state: StoreState, isbn: string) => state.productState.products[isbn]?.isSuccess;
export const isFailure = (state: StoreState, isbn: string) => state.productState.products[isbn]?.isFailure;
export const getProductIsbn = (state: StoreState, isbn: string) => state.productState.products[isbn]?.data?.isbn;

/*----- EXPORT DEFAULT ------*/
export const productReducer = productSlice.reducer;

/*----- HELPERS -----*/
export async function makeProductRequest(businessUnit: string, isbn: string) {
  const response = await fetch(`${environment().searchServiceUrl}/productdata/${businessUnit}/${isbn}`, {
    method: 'GET',
    credentials: 'include',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { message: response.statusText };
    }
    throw new Error(
      `Failed to load product: ${response.status} ${errorBody?.message || errorBody || response.statusText}`
    );
  }

  return await response.json();
}